import { todayIso, walletTypes, type Dashboard, type User, type Wallet, type WalletType } from '@fin/shared';
import { useState } from 'react';
import { TransactionModal } from '../transactions/public.ts';
import { Button, ConfirmButton, ErrorBox, Field, Icon, Input, Loading, Menu, Modal, Money, NumberInput, PageHead, Progress, Section, Select, Tile, cx, toast, useAction } from '../../components/ui.tsx';
import { authStore, del, patch, post, setUser } from '../../lib/api.ts';
import { useCurrencies, useRates, useWallets, walletTypeOrder } from '../../lib/data.ts';
import { plural, rub, walletTypeLabels } from '../../lib/format.ts';
import { walletIcon } from '../../lib/icons.tsx';
import { invalidate, invalidateMoney, useQuery } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';

export function WalletsPage() {
  const q = useWallets();
  const rates = useRates();
  const user = authStore.get().user;
  const [edit, setEdit] = useState<Wallet | 'new' | null>(null);
  const [adjust, setAdjust] = useState<Wallet | null>(null);
  const [transfer, setTransfer] = useState(false);
  if (q.error && !q.data) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q.data) return <Loading />;

  const bySort = (a: Wallet, b: Wallet) => a.sort - b.sort || a.id - b.id;
  const active = q.data.filter((w) => !w.archived && !w.hidden).sort(bySort);
  const hidden = q.data.filter((w) => !w.archived && w.hidden).sort(bySort);
  const archived = q.data.filter((w) => w.archived).sort(bySort);
  const total = q.data.filter((w) => !w.archived && !w.excludeFromTotal).reduce((s, w) => s + (w.balanceBase ?? 0), 0);
  const types = walletTypeOrder(user?.walletTypeOrder);
  const byType = types.map((t) => ({ type: t, list: active.filter((w) => w.type === t) })).filter((g) => g.list.length);
  const primaryId = user?.primaryWalletId ?? null;

  // groups are reordered through the profile; wallets inside a group through their sort
  const moveGroup = async (type: WalletType, dir: -1 | 1) => {
    const shown = byType.map((g) => g.type);
    const i = shown.indexOf(type);
    const j = i + dir;
    if (j < 0 || j >= shown.length) return;
    const next = [...shown];
    [next[i], next[j]] = [next[j], next[i]];
    // keep groups without wallets after the visible ones, in their current relative order
    const order = [...next, ...types.filter((t) => !shown.includes(t))];
    try {
      setUser(await patch<User>('/me', { walletTypeOrder: order }));
      invalidate('/dashboard');
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const moveWallet = async (w: Wallet, dir: -1 | 1) => {
    const group = active.filter((x) => x.type === w.type);
    const i = group.findIndex((x) => x.id === w.id);
    const j = i + dir;
    if (j < 0 || j >= group.length) return;
    const next = [...group];
    [next[i], next[j]] = [next[j], next[i]];
    try {
      await post('/wallets/reorder', { ids: next.map((x) => x.id) });
      invalidateMoney();
    } catch (e) { toast((e as Error).message, 'error'); }
  };

  const makePrimary = async (w: Wallet) => {
    try {
      setUser(await patch<User>('/me', { primaryWalletId: w.id }));
      invalidate('/dashboard');
      toast(`${w.name} — основной кошелёк`);
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const setHidden = async (w: Wallet, value: boolean) => {
    try {
      await patch(`/wallets/${w.id}`, { hidden: value });
      invalidateMoney();
      toast(value ? `${w.name} скрыт` : `${w.name} снова в списке`);
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const row = (w: Wallet, movable = false) => {
    const group = active.filter((x) => x.type === w.type);
    const i = group.findIndex((x) => x.id === w.id);
    return (
      <WalletRow key={w.id} w={w} base={rates.base} primary={w.id === primaryId}
        onEdit={() => setEdit(w)} onAdjust={() => setAdjust(w)} onPrimary={() => void makePrimary(w)} onHide={(v) => void setHidden(w, v)}
        onMove={movable ? { up: i > 0 ? () => void moveWallet(w, -1) : undefined, down: i >= 0 && i < group.length - 1 ? () => void moveWallet(w, 1) : undefined } : undefined} />
    );
  };

  const primary = q.data.find((w) => w.id === primaryId && !w.archived);

  return (
    <div className="page">
      <PageHead title="Счета" actions={<>
        <Button onClick={() => setTransfer(true)}><Icon name="transfer" /> Перевод</Button>
        <Button variant="primary" onClick={() => setEdit('new')}><Icon name="plus" /> Кошелёк</Button>
      </>} />

      <div className="accounts-top">
        <section className="section worth" aria-label="Все деньги">
          <span className="hero-label">Всего на счетах</span>
          <span className="worth-big money">{rub(total)}</span>
          {primary ? (
            <span className="worth-primary">
              <Tile icon={walletIcon(primary)} color={primary.color} size="sm" />
              <span>{primary.name}</span>
              <span className="badge">основной</span>
              <Money value={primary.balance} currency={primary.currency} className="worth-primary-sum" />
            </span>
          ) : <span className="muted small">Основной кошелёк выбирается в меню ⋯ у кошелька.</span>}
        </section>
        <DebtsCard />
      </div>

      {byType.map((g, gi) => (
        <Section key={g.type} title={walletTypeLabels[g.type]} aside={
          <span className="section-total">
            <strong className="money">{rub(g.list.reduce((s, w) => s + (w.balanceBase ?? 0), 0))}</strong>
            <span className="group-move">
              <button type="button" className="icon-btn" aria-label={`Группу «${walletTypeLabels[g.type]}» выше`} disabled={gi === 0} onClick={() => void moveGroup(g.type, -1)}><Icon name="up" size={16} /></button>
              <button type="button" className="icon-btn" aria-label={`Группу «${walletTypeLabels[g.type]}» ниже`} disabled={gi === byType.length - 1} onClick={() => void moveGroup(g.type, 1)}><Icon name="down" size={16} /></button>
            </span>
          </span>
        }>
          <ul className="wallets">{g.list.map((w) => row(w, true))}</ul>
        </Section>
      ))}
      {!byType.length ? <Section title="Кошельки"><p className="muted">Кошельков пока нет.</p></Section> : null}

      {hidden.length ? (
        <details className="archived">
          <summary>Скрытые ({hidden.length}) · <span className="muted small">учитываются в балансе, не показываются в списках</span></summary>
          <ul className="wallets">{hidden.map((w) => row(w))}</ul>
        </details>
      ) : null}
      {archived.length ? (
        <details className="archived">
          <summary>Архив ({archived.length})</summary>
          <ul className="wallets">{archived.map((w) => row(w))}</ul>
        </details>
      ) : null}

      {edit ? <WalletModal wallet={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} /> : null}
      {adjust ? <AdjustModal wallet={adjust} onClose={() => setAdjust(null)} /> : null}
      {transfer ? <TransactionModal defaults={{ mode: 'transfer' }} onClose={() => setTransfer(false)} /> : null}
    </div>
  );
}

/** debts in one glance: what is owed, the monthly load and how much is paid off */
function DebtsCard() {
  const dash = useQuery<Dashboard>('/dashboard');
  const d = dash.data?.debts;
  const total = d ? d.borrowed.reduce((s, x) => s + x.total, 0) : 0;
  const paid = d ? d.borrowed.reduce((s, x) => s + x.paid, 0) : 0;
  return (
    <Link to="/debts" className="section debts-card">
      <span className="debts-card-head"><span className="hero-label">Долги</span><Icon name="right" size={16} /></span>
      {!d ? <span className="skeleton sk-line" /> : d.borrowed.length || d.lent.length ? (
        <>
          <dl className="debts-card-nums">
            <div><dt>Я должен</dt><dd className="money">{rub(d.totalBorrowed)}</dd></div>
            <div><dt>В месяц</dt><dd className="money">{rub(d.monthlyLoad)}</dd></div>
            {d.lent.length ? <div><dt>Мне должны</dt><dd className="money">{rub(d.totalLent)}</dd></div> : null}
          </dl>
          {d.borrowed.length ? (
            <>
              <Progress value={total > 0 ? paid / total : 0} tone="debt" label="Выплачено по долгам" />
              <span className="muted small">выплачено {Math.round((total > 0 ? paid / total : 0) * 100)}% · {d.borrowed.length} {plural(d.borrowed.length, 'долг', 'долга', 'долгов')}</span>
            </>
          ) : null}
        </>
      ) : <span className="muted small">Активных долгов нет.</span>}
    </Link>
  );
}

function WalletRow({ w, base, primary, onEdit, onAdjust, onPrimary, onHide, onMove }: {
  w: Wallet; base: string; primary: boolean; onEdit: () => void; onAdjust: () => void; onPrimary: () => void; onHide: (hidden: boolean) => void;
  onMove?: { up?: () => void; down?: () => void };
}) {
  return (
    <li className={cx('wallet', w.archived && 'muted')}>
      <Tile icon={walletIcon(w)} color={w.color} />
      <Link to={`/transactions?wallet=${w.id}`} className="wallet-name">
        <span>{w.name}{primary ? <span className="badge">основной</span> : null}</span>
        {w.excludeFromTotal ? <span className="muted small">не входит в итог</span> : null}
      </Link>
      <span className="wallet-balance">
        <Money value={w.balance} currency={w.currency} />
        {w.currency !== base ? <span className="muted small money">{rub(w.balanceBase)}</span> : null}
      </span>
      <span className="wallet-actions">
        <Menu label={`Действия: ${w.name}`} items={[
          ...(!primary && !w.archived ? [{ label: 'Сделать основным', icon: 'pin', onClick: onPrimary }] : []),
          ...(onMove?.up ? [{ label: 'Выше в группе', icon: 'up', onClick: onMove.up }] : []),
          ...(onMove?.down ? [{ label: 'Ниже в группе', icon: 'down', onClick: onMove.down }] : []),
          { label: 'Изменить остаток', icon: 'sliders', onClick: onAdjust },
          ...(!w.archived ? [{ label: w.hidden ? 'Показывать в списках' : 'Скрыть из списков', icon: w.hidden ? 'eye' : 'eye-off', onClick: () => onHide(!w.hidden) }] : []),
          { label: 'Настроить', icon: 'edit', onClick: onEdit },
        ]} />
      </span>
    </li>
  );
}

const colors = ['#7EB6FF', '#5EE6A8', '#F2C14E', '#F28B8B', '#B69CFF', '#4FD1C5', '#F49D6E', '#C8D0DC'];

function WalletModal({ wallet, onClose }: { wallet?: Wallet; onClose: () => void }) {
  const currencies = useCurrencies();
  const [name, setName] = useState(wallet?.name ?? '');
  const [currency, setCurrency] = useState(wallet?.currency ?? 'RUB');
  const [type, setType] = useState<WalletType>(wallet?.type ?? 'bank');
  const [initial, setInitial] = useState<number | null>(wallet?.initialBalance ?? 0);
  const [color, setColor] = useState(wallet?.color ?? colors[0]);
  const [exclude, setExclude] = useState(wallet?.excludeFromTotal ?? false);
  const [hidden, setHidden] = useState(wallet?.hidden ?? false);
  const [archived, setArchived] = useState(wallet?.archived ?? false);
  const { busy, run } = useAction();
  const save = () => run(async () => {
    const body = { name, currency, type, initialBalance: initial ?? 0, color, excludeFromTotal: exclude, hidden, archived };
    if (wallet) await patch(`/wallets/${wallet.id}`, body); else await post('/wallets', body);
    invalidateMoney();
    onClose();
  }, 'Кошелёк сохранён');

  return (
    <Modal className="modal-form" title={wallet ? wallet.name : 'Новый кошелёк'} onClose={onClose} footer={<>
      {wallet ? <ConfirmButton size="md" confirmText={wallet.transactionCount ? `Удалить вместе с ${wallet.transactionCount} операциями?` : 'Точно удалить?'}
        onConfirm={async () => { await del(`/wallets/${wallet.id}${wallet.transactionCount ? '?force=true' : ''}`); invalidateMoney(); onClose(); }}>Удалить</ConfirmButton> : <span />}
      <div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Название"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required /></Field>
        <div className="form-row">
          <Field label="Валюта" className="grow">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={!!wallet?.transactionCount}>
              {(currencies.data ?? [{ code: currency, name: currency }]).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </Select>
          </Field>
          <Field label="Тип" className="grow" hint={type === 'investment' ? 'Переводы сюда считаются пополнением инвестиций в финплане' : undefined}>
            <Select value={type} onChange={(e) => setType(e.target.value as WalletType)}>
              {walletTypes.map((t) => <option key={t} value={t}>{walletTypeLabels[t]}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Начальный остаток" hint="Остаток до первой операции в трекере"><NumberInput value={initial} onChange={setInitial} /></Field>
        <div className="field">
          <span className="field-label">Цвет</span>
          <div className="colors">
            {colors.map((c) => <button type="button" key={c} className={cx('color', c === color && 'on')} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Цвет ${c}`} aria-pressed={c === color} />)}
          </div>
        </div>
        <label className="check"><input type="checkbox" checked={exclude} onChange={(e) => setExclude(e.target.checked)} /> Не учитывать в общем балансе</label>
        <label className="check"><input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> Скрыть из списков (остаётся в балансе и при выборе кошелька)</label>
        {wallet ? <label className="check"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> В архиве</label> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function AdjustModal({ wallet, onClose }: { wallet: Wallet; onClose: () => void }) {
  const [balance, setBalance] = useState<number | null>(wallet.balance);
  const [date, setDate] = useState(todayIso());
  const { busy, run } = useAction();
  return (
    <Modal className="modal-form" title={`Остаток: ${wallet.name}`} onClose={onClose} footer={<>
      <span /><div className="row-gap"><Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" busy={busy} onClick={() => run(async () => {
          await post(`/wallets/${wallet.id}/adjust`, { balance, date });
          invalidateMoney();
          onClose();
        }, 'Остаток изменён')}>Применить</Button></div>
    </>}>
      <p className="muted">Разница запишется корректирующей операцией «Изменить остаток» и не попадёт в отчёты.</p>
      <div className="form">
        <Field label={`Фактический остаток, ${wallet.currency}`}><NumberInput value={balance} onChange={setBalance} autoFocus /></Field>
        <Field label="Дата"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
